import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  GTG_KENYA_COUNTIES,
  parseKenyaCountiesText,
  resolveKenyaCountyCanonical,
} from "@healthdesk/shared-utils";
import {
  getKenyaCountyGoogleAdsDisplayName,
  getKenyaCountyTargetingSnapshot,
  KENYA_COUNTY_CENSUS_VINTAGE_LABEL,
  KENYA_FACILITY_ACCESS_VINTAGE_LABEL,
  KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE,
  KENYA_KDHS_2022_VINTAGE_LABEL,
} from "../../services/census/getKenyaCountyCensusSnapshot";

/** @typedef {'percent' | 'integer' | 'decimal'} KenyaMetricFormat */

/** @type {{ key: string, label: string, format: KenyaMetricFormat }[]} */
const KENYA_FOCUS_METRICS = [
  { key: "pctMale", label: "% male (total pop., KNBS 2019)", format: "percent" },
  { key: "pctFemale", label: "% female (total pop., KNBS 2019)", format: "percent" },
  { key: "totalPopulation", label: "Total population (KNBS 2019)", format: "integer" },
  { key: "populationDensitySqKm", label: "Population density / km² (KNBS 2019)", format: "integer" },
  { key: "landAreaSqKm", label: "Land area (km², KNBS 2019)", format: "decimal" },
  {
    key: "facilitiesPer100k",
    label: "Facilities per 100k pop. (KMHFL operational ÷ KNBS 2019 pop.)",
    format: "decimal",
  },
  { key: "facilitiesOperational", label: "Operational facilities (KMHFL registry)", format: "integer" },
  { key: "hospitals", label: "Hospital / referral-type facilities (KMHFL bucket)", format: "integer" },
  { key: "healthCentres", label: "PHC centre–type facilities (KMHFL bucket)", format: "integer" },
  { key: "dispensariesClinics", label: "Dispensary / clinic-type facilities (KMHFL bucket)", format: "integer" },
  { key: "ownerPublicSector", label: "Public-sector-owned facilities (KMHFL)", format: "integer" },
  { key: "ownerPrivateForProfit", label: "Private for-profit facilities (KMHFL)", format: "integer" },
  {
    key: "under5MortalityPer1000",
    label: "Under-5 mortality (per 1,000 live births, KDHS 2022 county)",
    format: "integer",
  },
  {
    key: "neonatalMortalityPer1000",
    label: "Neonatal mortality (per 1,000 live births, KDHS 2022 county)",
    format: "integer",
  },
  {
    key: "infantMortalityPer1000",
    label: "Infant mortality (per 1,000 live births, KDHS 2022 county)",
    format: "integer",
  },
  {
    key: "skilledBirthPct",
    label: "Births with skilled provider (%, KDHS 2022 county)",
    format: "percent",
  },
  { key: "anc4PlusPct", label: "4+ ANC visits (%, KDHS 2022 county)", format: "percent" },
  {
    key: "kdhsFullVacc12to23mPct",
    label: "Children 12–23m fully vaccinated — basic antigens (%, KDHS 2022 county)",
    format: "percent",
  },
];

function defaultMetricKey() {
  return KENYA_FOCUS_METRICS[0]?.key ?? "pctMale";
}

/**
 * @param {number|null|undefined} n
 * @param {KenyaMetricFormat} format
 */
function formatMetricDisplay(n, format) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (format === "percent") return `${n.toFixed(1)}%`;
  if (format === "decimal") return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return `${Math.round(n).toLocaleString()}`;
}

/** @param {Record<string, unknown>|null|undefined} snap */
function metricNumeric(snap, metricKey) {
  if (!snap || typeof snap !== "object") return null;
  const v = snap[metricKey];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Same token split as `parseKenyaCountiesText` so comma / semicolon / newline lists stay compatible.
 * @returns {{ raw: string, canonical: string|null }[]}
 */
function parseOrderedCountyLines(text) {
  const parts =
    typeof text === "string" && text.trim()
      ? text
          .split(/[\n,;]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  return parts.map((raw) => ({
    raw,
    canonical: resolveKenyaCountyCanonical(raw, GTG_KENYA_COUNTIES),
  }));
}

function valueFromLines(lines) {
  return lines.map((l) => (l.canonical ? l.canonical : l.raw)).join("\n");
}

function sortLinesByMetric(lines, snapshots, metricKey, ascending) {
  const indexed = lines.map((line, idx) => ({ line, idx }));
  const known = indexed.filter((x) => x.line.canonical);
  const unknown = indexed.filter((x) => !x.line.canonical);
  known.sort((a, b) => {
    const na = metricNumeric(snapshots[a.line.canonical], metricKey);
    const nb = metricNumeric(snapshots[b.line.canonical], metricKey);
    const aNull = na == null;
    const bNull = nb == null;
    if (aNull && bNull) return a.idx - b.idx;
    if (aNull) return 1;
    if (bNull) return -1;
    if (na === nb) return a.idx - b.idx;
    return ascending ? na - nb : nb - na;
  });
  return [...known, ...unknown].map((x) => x.line);
}

/**
 * Kenya county list: delete-only row UI, KNBS 2019 focus metric chart, sort.
 * Add counties via the checklist (or saved collections on pages that support them).
 *
 * @param {{
 *   value: string,
 *   onChange: (next: string) => void,
 *   disabled?: boolean,
 *   label?: string,
 *   minRows?: number,
 *   showQuickAdd?: boolean,
 *   sx?: object,
 * }} props
 */
const CountyTargetingField = ({
  value,
  onChange,
  disabled = false,
  label = "Kenya counties in this group",
  minRows: _minRows,
  showQuickAdd: _showQuickAdd,
  sx = {},
}) => {
  const theme = useTheme();
  const lines = useMemo(() => parseOrderedCountyLines(value), [value]);
  const parsedSummary = useMemo(() => parseKenyaCountiesText(value || ""), [value]);

  const [focusMetric, setFocusMetric] = useState(defaultMetricKey);
  const metricMeta = useMemo(
    () =>
      KENYA_FOCUS_METRICS.find((m) => m.key === focusMetric) ?? KENYA_FOCUS_METRICS[0],
    [focusMetric]
  );

  const snapshots = useMemo(() => {
    /** @type {Record<string, Record<string, unknown>|null>} */
    const m = {};
    for (const c of GTG_KENYA_COUNTIES) {
      m[c] = getKenyaCountyTargetingSnapshot(c);
    }
    return m;
  }, []);

  const removeAt = useCallback(
    (idx) => {
      const next = lines.filter((_, i) => i !== idx);
      onChange(valueFromLines(next));
    },
    [lines, onChange]
  );

  const applySort = useCallback(
    (ascending) => {
      const sorted = sortLinesByMetric(lines, snapshots, focusMetric, ascending);
      onChange(valueFromLines(sorted));
    },
    [lines, snapshots, focusMetric, onChange]
  );

  const chartData = useMemo(() => {
    if (!metricMeta || lines.length === 0) return [];
    return lines.map((line, i) => {
      const name = line.canonical || line.raw;
      const snap = line.canonical ? snapshots[line.canonical] : null;
      const raw = metricNumeric(snap, focusMetric);
      const short = name.length > 14 ? `${name.slice(0, 12)}…` : name;
      return {
        key: `${name}-${i}`,
        label: `${i + 1}. ${short}`,
        fullLabel: name,
        value: raw,
        chartValue: raw == null ? null : raw,
        display: formatMetricDisplay(raw, metricMeta.format),
      };
    });
  }, [lines, snapshots, focusMetric, metricMeta]);

  const chartHeight = Math.min(520, Math.max(140, lines.length * 26 + 80));

  const chartTooltip = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          }}
        >
          <Typography variant="caption" display="block" fontWeight={600}>
            {row.fullLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {metricMeta?.label}: {row.display}
          </Typography>
        </Box>
      );
    },
    [metricMeta]
  );

  const unknown = parsedSummary.unknown;

  return (
    <Box sx={sx}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        <strong>{parsedSummary.counties.length}</strong> recognized{" "}
        {parsedSummary.counties.length === 1 ? "county" : "counties"}
        {lines.length !== parsedSummary.counties.length ? (
          <>
            {" "}
            · <strong>{lines.length}</strong> line{lines.length === 1 ? "" : "s"} in list
          </>
        ) : null}
        . Add counties from the map or checklist below (or a saved collection where available); remove
        with the trash control. {KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE} Census sex shares are % of total
        published county population (rounding may prevent exact 100%). Other metrics are labeled by source
        in the Focus metric list.
      </Typography>
      {unknown.length > 0 ? (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1.5 }}>
          Unrecognized — fix or remove: <strong>{unknown.join(", ")}</strong>
        </Typography>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <FormControl size="small" sx={{ minWidth: 300 }} disabled={disabled}>
          <InputLabel id="kenya-focus-metric-label">Focus metric</InputLabel>
          <Select
            labelId="kenya-focus-metric-label"
            label="Focus metric"
            value={focusMetric}
            onChange={(e) => setFocusMetric(String(e.target.value))}
          >
            {KENYA_FOCUS_METRICS.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Sort list low → high by this metric (missing / unrecognized last)">
            <span>
              <IconButton
                size="small"
                aria-label="Sort ascending by focus metric"
                disabled={disabled || lines.length < 2}
                onClick={() => applySort(true)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sort list high → low by this metric (missing / unrecognized last)">
            <span>
              <IconButton
                size="small"
                aria-label="Sort descending by focus metric"
                disabled={disabled || lines.length < 2}
                onClick={() => applySort(false)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {lines.length > 0 ? (
        <Box sx={{ width: "100%", height: chartHeight, maxWidth: "100%", mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                stroke={theme.palette.text.secondary}
                domain={[0, "auto"]}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={88}
                tick={{ fontSize: 10 }}
                stroke={theme.palette.text.secondary}
              />
              <RechartsTooltip content={chartTooltip} />
              <Bar
                dataKey="chartValue"
                name={metricMeta?.label}
                fill={theme.palette.primary.main}
                radius={[0, 4, 4, 0]}
                isAnimationActive={lines.length <= 40}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {KENYA_COUNTY_CENSUS_VINTAGE_LABEL} {KENYA_FACILITY_ACCESS_VINTAGE_LABEL} {KENYA_KDHS_2022_VINTAGE_LABEL}
      </Typography>

      {lines.length > 0 ? (
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 280,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {lines.map((line, idx) => {
            const snap = line.canonical ? snapshots[line.canonical] : null;
            const num = metricNumeric(snap, focusMetric);
            const secondary = line.canonical
              ? formatMetricDisplay(num, metricMeta?.format ?? "percent")
              : "— (not in county list)";
            const primary = line.canonical
              ? getKenyaCountyGoogleAdsDisplayName(line.canonical)
              : line.raw;
            return (
              <ListItem
                key={`${line.raw}-${idx}`}
                secondaryAction={
                  <Tooltip title="Remove from list">
                    <span>
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${primary}`}
                        size="small"
                        disabled={disabled}
                        onClick={() => removeAt(idx)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemText primary={primary} secondary={secondary} />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No counties yet. Use the checklist below to add counties.
        </Typography>
      )}
    </Box>
  );
};

export default CountyTargetingField;
